import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Trash2, GripVertical, Eye, EyeOff, Search, Upload } from 'lucide-react';
import { sanitizeFileName } from '@/lib/utils';

interface GalleryItem {
  id: string;
  title: string;
  description: string;
  image_url: string;
  sort_order: number;
  visible: boolean;
}

const GalleryAdmin = () => {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [search, setSearch] = useState('');
  const dragIndex = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const fetchItems = async () => {
    const { data } = await supabase
      .from('gallery_items')
      .select('*')
      .order('sort_order', { ascending: true });
    if (data) setItems(data);
  };

  useEffect(() => { fetchItems(); }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });

    const startOrder = items.length > 0 ? Math.max(...items.map(i => i.sort_order)) + 1 : 0;
    let success = 0;
    let failed = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: files.length });
      const safeName = sanitizeFileName(file.name);
      const fileName = `${Date.now()}-${i}-${safeName}`;
      const { error: uploadError } = await supabase.storage.from('gallery').upload(fileName, file);
      if (uploadError) { failed++; continue; }
      const { data: urlData } = supabase.storage.from('gallery').getPublicUrl(fileName);
      const { error: insertError } = await supabase.from('gallery_items').insert({
        title: file.name.replace(/\.[^.]+$/, ''),
        image_url: urlData.publicUrl,
        sort_order: startOrder + i,
      });
      if (insertError) failed++; else success++;
    }

    if (success > 0) toast.success(`Nahráno ${success} fotek${failed > 0 ? ` (${failed} selhalo)` : ''}`);
    if (success === 0 && failed > 0) toast.error(`Nahrávání selhalo (${failed})`);
    fetchItems();
    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });
    e.target.value = '';
  };

  const updateItem = async (id: string, updates: Partial<GalleryItem>) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...updates } : i));
    await supabase.from('gallery_items').update(updates).eq('id', id);
  };

  const deleteItem = async (id: string, imageUrl: string) => {
    if (!confirm('Opravdu smazat?')) return;
    const fileName = imageUrl.split('/').pop();
    if (fileName) await supabase.storage.from('gallery').remove([fileName]);
    await supabase.from('gallery_items').delete().eq('id', id);
    toast.success('Smazáno');
    fetchItems();
  };

  // Drag & drop reordering
  const handleDragStart = (index: number) => { dragIndex.current = index; };
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOver(index);
  };
  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOver(null);
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === dropIndex) return;

    const updated = [...items];
    const [moved] = updated.splice(from, 1);
    updated.splice(dropIndex, 0, moved);
    setItems(updated);

    // Persist new sort_order
    const promises = updated.map((item, i) =>
      supabase.from('gallery_items').update({ sort_order: i }).eq('id', item.id)
    );
    await Promise.all(promises);
    toast.success('Pořadí uloženo');
  };

  const filtered = items.filter(i =>
    i.title.toLowerCase().includes(search.toLowerCase()) ||
    (i.description || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <h1 className="text-4xl tracking-wider text-foreground" style={{ fontFamily: 'Bebas Neue' }}>
          Galerie ({items.length})
        </h1>
        <label className="cursor-pointer">
          <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
          <Button asChild disabled={uploading}>
            <span className="inline-flex items-center gap-2">
              {uploading ? <Upload size={18} className="animate-pulse" /> : <Plus size={18} />}
              {uploading ? `Nahrávám ${uploadProgress.current}/${uploadProgress.total}...` : 'Přidat fotky'}
            </span>
          </Button>
        </label>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Hledat podle názvu nebo popisu..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 bg-background border-border"
        />
      </div>

      <p className="text-xs text-muted-foreground mb-3">
        💡 Tip: Tažením za <GripVertical size={12} className="inline" /> můžete přesouvat položky a měnit pořadí.
      </p>

      <div className="space-y-3">
        {filtered.map((item) => {
          const realIndex = items.indexOf(item);
          return (
            <div
              key={item.id}
              draggable={!search}
              onDragStart={() => handleDragStart(realIndex)}
              onDragOver={(e) => handleDragOver(e, realIndex)}
              onDrop={(e) => handleDrop(e, realIndex)}
              onDragEnd={() => setDragOver(null)}
              className={`flex items-center gap-4 p-4 bg-card border rounded-md transition-all ${
                dragOver === realIndex ? 'border-primary scale-[1.01]' : 'border-border'
              } ${!search ? 'cursor-move' : ''}`}
            >
              <GripVertical size={20} className="text-muted-foreground shrink-0" />
              <img src={item.image_url} alt={item.title} className="w-20 h-20 object-cover rounded shrink-0" loading="lazy" />
              <div className="flex-1 space-y-2 min-w-0">
                <Input
                  value={item.title}
                  onChange={(e) => updateItem(item.id, { title: e.target.value })}
                  placeholder="Název"
                  className="bg-background border-border text-sm"
                />
                <Textarea
                  value={item.description || ''}
                  onChange={(e) => updateItem(item.id, { description: e.target.value })}
                  placeholder="Popis"
                  rows={1}
                  className="bg-background border-border text-sm"
                />
              </div>
              <button
                onClick={() => updateItem(item.id, { visible: !item.visible })}
                className={`p-2 rounded transition-colors shrink-0 ${item.visible ? 'text-primary' : 'text-muted-foreground'}`}
                title={item.visible ? 'Skrýt' : 'Zobrazit'}
              >
                {item.visible ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
              <button
                onClick={() => deleteItem(item.id, item.image_url)}
                className="p-2 text-muted-foreground hover:text-destructive transition-colors shrink-0"
                title="Smazat"
              >
                <Trash2 size={18} />
              </button>
            </div>
          );
        })}
        {filtered.length === 0 && items.length > 0 && (
          <p className="text-center text-muted-foreground py-12">Žádné výsledky pro "{search}".</p>
        )}
        {items.length === 0 && (
          <p className="text-center text-muted-foreground py-12">Zatím žádné fotky. Klikněte na "Přidat fotky".</p>
        )}
      </div>
    </div>
  );
};

export default GalleryAdmin;
